import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Minus, Plus } from "lucide-react";

import { productsService } from "../../../../services/products";
import { ordersService } from "../../../../services/orders";
import { Button } from "../../../../components/ui/button";

export function StepProducts({ form, setForm, t }) {
  const [products, setProducts] = useState([]);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  // t may be passed from a parent that also owns i18n; grab it as a fallback
  // for standalone use.
  const { t: fallbackT } = useTranslation();
  const _t = t || fallbackT;

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    // Guard against invalid ranges before spending an API call.
    const validRange =
      form.from_date && form.until_date && form.from_date <= form.until_date;
    Promise.all([
      productsService.list({ page_size: 100 }),
      validRange
        ? ordersService.availability(form.from_date, form.until_date)
        : Promise.resolve({ products: [] }),
    ])
      .then(([p, a]) => {
        if (!mounted) return;
        setProducts(p.items || []);
        const map = {};
        (a.products || []).forEach((x) => (map[x.product_id] = x));
        setAvailability(map);
      })
      .catch((e) => {
        if (!mounted) return;
        const msg = e?.response?.data?.detail || _t("common.error_generic");
        toast.error(typeof msg === "string" ? msg : _t("common.error_generic"));
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [form.from_date, form.until_date, _t]);

  const qty = (product_id) =>
    form.items.find((i) => i.product_id === product_id)?.quantity || 0;

  const setQty = (product, next) => {
    const clamped = Math.max(0, next);
    const others = form.items.filter((i) => i.product_id !== product.id);
    const image_urls_snapshot = Array.isArray(product.image_urls)
      ? product.image_urls
      : product.image_url
      ? [product.image_url]
      : [];
    const items =
      clamped === 0
        ? others
        : [
            ...others,
            {
              product_id: product.id,
              quantity: clamped,
              product_name_snapshot: product.name,
              product_image_snapshot: image_urls_snapshot[0] || null,
              image_urls_snapshot,
            },
          ];
    setForm({ ...form, items });
  };

  return (
    <div className="space-y-4" data-testid="step-products">
      {loading ? (
        <div className="text-sm text-text-secondary">{t("common.loading")}</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {products.map((p) => {
            const q = qty(p.id);
            const av = availability[p.id];
            const availMin = av?.available_min ?? p.in_stock;
            const chip =
              q === 0
                ? { text: `${availMin} ${t("wizard.available")}`, cls: "bg-offwhite text-text-secondary" }
                : q > availMin
                  ? { text: t("wizard.avail_exceeded"), cls: "bg-status-unavailable/10 text-status-unavailable" }
                  : { text: `${q}/${availMin} ${t("wizard.selected")}`, cls: "bg-status-available/10 text-status-available" };
            return (
              <div
                key={p.id}
                data-testid={`wizard-product-${p.id}`}
                className="flex gap-3 rounded-lg border border-border-subtle bg-surface p-3"
              >
                <div className="h-20 w-20 flex-none overflow-hidden rounded bg-offwhite">
                  {((Array.isArray(p.image_urls) && p.image_urls[0]) || p.image_url) && (
                    <img
                      src={(Array.isArray(p.image_urls) && p.image_urls[0]) || p.image_url}
                      alt={p.name}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-text-primary">{p.name}</div>
                  <div className="mt-1 text-xs text-text-secondary">
                    {[p.brand, p.category].filter(Boolean).join(" · ")}
                  </div>
                  <span
                    className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${chip.cls}`}
                    data-testid={`wizard-product-avail-${p.id}`}
                  >
                    {chip.text}
                  </span>
                  <div className="mt-2 flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setQty(p, q - 1)}
                      disabled={q === 0}
                      data-testid={`wizard-product-minus-${p.id}`}
                      className="h-8 w-8 p-0"
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span
                      className="w-8 text-center text-sm font-semibold"
                      data-testid={`wizard-product-qty-${p.id}`}
                    >
                      {q}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setQty(p, q + 1)}
                      disabled={q >= availMin}
                      data-testid={`wizard-product-plus-${p.id}`}
                      className="h-8 w-8 p-0"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div
        className="rounded-lg border border-border-subtle bg-offwhite/40 px-4 py-3 text-sm"
        data-testid="wizard-cart-summary"
      >
        <span className="font-semibold">{form.items.length}</span> {t("wizard.products_selected")} ·{" "}
        <span className="font-semibold">{form.items.reduce((s, i) => s + i.quantity, 0)}</span>{" "}
        {t("wizard.units")}
      </div>
    </div>
  );
}
